import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import Navbar from "@/components/site/Navbar";
import Hero from "@/components/site/Hero";
import Accompagnement from "@/components/site/Accompagnement";
import Clients from "@/components/site/Clients";
import Values from "@/components/site/Values";
import About from "@/components/site/About";
import Branding from "@/components/site/Branding";
import WebSection from "@/components/site/WebSection";
import Audit from "@/components/site/Audit";
import Pricing from "@/components/site/Pricing";
import CtaFinal from "@/components/site/CtaFinal";
import Faq from "@/components/site/Faq";
import Footer from "@/components/site/Footer";
import ChatBot from "@/components/site/ChatBot";
import QuoteDialog from "@/components/site/QuoteDialog";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HOME_TITLE = "EM Group — Agence digitale premium";
const HOME_DESCRIPTION =
  "EM Group — Agence digitale premium : branding, sites web, visuels, stratégie. Donnez à votre entreprise une image à la hauteur de vos ambitions.";

function setHomeMeta(name, content, attr = "name") {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function Home() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    document.title = HOME_TITLE;
    setHomeMeta("description", HOME_DESCRIPTION);
    setHomeMeta("og:title", HOME_TITLE, "property");
    setHomeMeta("og:description", HOME_DESCRIPTION, "property");
    setHomeMeta("og:url", "https://emgroupdigital.fr/", "property");
    setHomeMeta("twitter:title", HOME_TITLE);
    setHomeMeta("twitter:description", HOME_DESCRIPTION);
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, paymentType, attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2000;
    if (attempts >= maxAttempts) {
      toast.error("Vérification du paiement expirée. Vérifiez votre email.");
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = res.data;

      if (data.payment_status === "paid") {
        toast.success(
          "Merci pour votre commande. Nous vous contactons sous 24h.",
          { duration: 8000 }
        );
        window.history.replaceState({}, document.title, "/");
        return;
      }
      if (data.status === "expired") {
        toast.error("Session de paiement expirée. Merci de réessayer.");
        window.history.replaceState({}, document.title, "/");
        return;
      }
      toast.info("Paiement en cours de traitement…", { id: "pay-pending" });
      setTimeout(
        () => pollPaymentStatus(sessionId, paymentType, attempts + 1),
        pollInterval
      );
    } catch (e) {
      toast.error("Erreur lors de la vérification du paiement.");
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cancelled = params.get("payment");
    const paymentType = params.get("type");
    if (sessionId) {
      toast.info("Vérification du paiement en cours…");
      pollPaymentStatus(sessionId, paymentType);
    } else if (cancelled === "cancelled") {
      toast.error(
        "Paiement annulé. Vous pouvez relancer votre commande à tout moment.",
        { duration: 6000 }
      );
      window.history.replaceState({}, document.title, "/");
    }
  }, [pollPaymentStatus]);

  const openQuote = () => setQuoteOpen(true);

  return (
    <div className="min-h-screen bg-[#050507] text-white overflow-x-hidden">
      <Navbar onOpenQuote={openQuote} />
      <Hero onOpenQuote={openQuote} />
      <Accompagnement onOpenQuote={openQuote} />
      <Clients />
      <Values />
      <About />
      <Branding onOpenQuote={openQuote} />
      <WebSection onOpenQuote={openQuote} />
      <Audit onOpenQuote={openQuote} />
      <Pricing />
      <CtaFinal onOpenQuote={openQuote} />
      <Faq onOpenQuote={openQuote} />
      <Footer />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
      <ChatBot />
    </div>
  );
}
